import React from "react";
import { InfoHint } from "components/common/InfoHint";
import { QuotaProgressBar } from "@/components/main/utils/QuotaProgressBar";
import type { MeteredUsage } from "@/lib/api/organizations";

/// `null` for an uncapped resource; a limit of zero is spent, not uncapped.
/// Unclamped past 100 so a caller can tell "over" from "full".
function rawMeterPercent(used: number, limit: number | null): number | null {
  if (limit === null) return null;
  if (limit <= 0) return used > 0 ? Infinity : 100;
  return (used / limit) * 100;
}

/// The count and the ceiling come from one meter, so they cannot be paired
/// from two.
export const UsageRow: React.FC<{
  label: string;
  info: string;
  meter: MeteredUsage;
  format: (value: number) => string;
  spentNote: string;
  action?: React.ReactNode;
}> = ({ label, info, meter, format, spentNote, action }) => {
  const percentage = rawMeterPercent(meter.used, meter.limit);
  const used = format(meter.used);
  const extra = meter.extra ?? 0;
  const cap =
    meter.limit === null
      ? null
      : extra > 0
        ? `${format(meter.limit - extra)} + ${format(extra)} extra`
        : format(meter.limit);
  const spent = percentage !== null && percentage >= 100;
  return (
    <div className="flex flex-col gap-2 px-2 py-2">
      <span className="flex items-baseline gap-2">
        <span className="text-fg-muted-medium inline-flex flex-1 items-center gap-1">
          {label}
          <InfoHint title={info} label={`What ${label} measures`} />
        </span>
        {percentage !== null && (
          <span className="text-fg-muted-regular shrink-0">
            {Number.isFinite(percentage)
              ? `${Math.round(percentage)}%`
              : ">100%"}
          </span>
        )}
        {action}
      </span>
      <span
        className="text-figure"
        data-testid={`usage-${label.toLowerCase().replace(/ /g, "-")}`}
      >
        {used}
        <span className="text-figure-muted">
          {cap === null ? " used" : ` of ${cap}`}
        </span>
      </span>
      <QuotaProgressBar percentage={percentage} />
      {spent && (
        <span className="text-fg-muted-regular-small">{spentNote}</span>
      )}
    </div>
  );
};
